import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/customers")({
  component: CustomersPage,
});

interface Customer {
  id: string;
  name: string;
  email: string;
  membershipId: string;
  tier: "Elite" | "Plus" | "Standard";
  tradeIns: number;
  creditsBalance: number; // in cents
  creditsLifetime: number;
  totalSpend: number; // in cents
  lastActivity: string;
  joinedDate: string;
  membershipLinked: boolean;
}

const CUSTOMERS: Customer[] = [
  { id: "c1", name: "Sophie Andersen", email: "sophie.a@gmail.com", membershipId: "ARC-00421", tier: "Elite", tradeIns: 7, creditsBalance: 42000, creditsLifetime: 89500, totalSpend: 312000, lastActivity: "2 days ago", joinedDate: "Mar 2023", membershipLinked: true },
  { id: "c2", name: "James Whitfield", email: "j.whitfield@icloud.com", membershipId: "ARC-01847", tier: "Elite", tradeIns: 5, creditsBalance: 31500, creditsLifetime: 65000, totalSpend: 284000, lastActivity: "1 week ago", joinedDate: "Jan 2023", membershipLinked: true },
  { id: "c3", name: "Mia Nakamura", email: "mia.naka@outlook.com", membershipId: "ARC-03291", tier: "Plus", tradeIns: 4, creditsBalance: 18000, creditsLifetime: 41200, totalSpend: 198000, lastActivity: "3 days ago", joinedDate: "Jun 2023", membershipLinked: true },
  { id: "c4", name: "Tobias Renn", email: "t.renn@protonmail.com", membershipId: "ARC-07654", tier: "Plus", tradeIns: 3, creditsBalance: 12500, creditsLifetime: 28700, totalSpend: 156000, lastActivity: "Today", joinedDate: "Sep 2023", membershipLinked: false },
  { id: "c5", name: "Clara Dupont", email: "clara.dupont@gmail.com", membershipId: "ARC-09103", tier: "Plus", tradeIns: 3, creditsBalance: 9000, creditsLifetime: 22400, totalSpend: 143000, lastActivity: "5 days ago", joinedDate: "Oct 2023", membershipLinked: true },
  { id: "c6", name: "Liam O'Brien", email: "liam.obrien@me.com", membershipId: "ARC-11258", tier: "Standard", tradeIns: 2, creditsBalance: 7500, creditsLifetime: 14800, totalSpend: 89000, lastActivity: "2 weeks ago", joinedDate: "Jan 2024", membershipLinked: true },
  { id: "c7", name: "Priya Mehta", email: "priya.m@gmail.com", membershipId: "ARC-14701", tier: "Standard", tradeIns: 2, creditsBalance: 6200, creditsLifetime: 12100, totalSpend: 74000, lastActivity: "1 week ago", joinedDate: "Feb 2024", membershipLinked: false },
  { id: "c8", name: "Noah Bergström", email: "n.bergstrom@gmail.com", membershipId: "ARC-18842", tier: "Standard", tradeIns: 1, creditsBalance: 4500, creditsLifetime: 4500, totalSpend: 52000, lastActivity: "3 weeks ago", joinedDate: "Mar 2024", membershipLinked: true },
  { id: "c9", name: "Amara Osei", email: "amara.osei@hotmail.com", membershipId: "ARC-22019", tier: "Standard", tradeIns: 1, creditsBalance: 3800, creditsLifetime: 3800, totalSpend: 41000, lastActivity: "1 month ago", joinedDate: "Apr 2024", membershipLinked: false },
  { id: "c10", name: "Elena Voronova", email: "elena.v@gmail.com", membershipId: "ARC-25634", tier: "Standard", tradeIns: 1, creditsBalance: 2900, creditsLifetime: 2900, totalSpend: 38000, lastActivity: "3 weeks ago", joinedDate: "May 2024", membershipLinked: true },
];

const TIER_CONFIG = {
  Elite: { bg: "rgba(0,251,169,0.12)", color: "#00A87A", border: "rgba(0,251,169,0.3)" },
  Plus: { bg: "rgba(100,149,237,0.1)", color: "#4A7FD4", border: "rgba(100,149,237,0.3)" },
  Standard: { bg: "rgba(169,169,169,0.1)", color: "#6B6B6B", border: "rgba(169,169,169,0.25)" },
};

function fmt(cents: number) {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
}

function fmtCredits(cents: number) {
  return `$${(cents / 100).toFixed(0)}`;
}

function TierBadge({ tier }: { tier: Customer["tier"] }) {
  const cfg = TIER_CONFIG[tier];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      {tier === "Elite" && (
        <svg width="9" height="9" viewBox="0 0 12 12" fill="currentColor">
          <path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5l3.5-.5z" />
        </svg>
      )}
      {tier}
    </span>
  );
}

export default function CustomersPage() {
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const filtered = CUSTOMERS.filter((c) => {
    const matchSearch =
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.membershipId.toLowerCase().includes(search.toLowerCase());
    const matchTier = tierFilter === "all" || c.tier === tierFilter;
    return matchSearch && matchTier;
  });

  const totalCreditsIssued = CUSTOMERS.reduce((s, c) => s + c.creditsLifetime, 0);
  const totalSpend = CUSTOMERS.reduce((s, c) => s + c.totalSpend, 0);
  const linkedCount = CUSTOMERS.filter((c) => c.membershipLinked).length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>Customers</h1>
          <p className="mt-1 text-sm" style={{ color: "#A9A9A9" }}>
            ReBird members — loyalty credits, spend history, and membership links
          </p>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-80"
          style={{ backgroundColor: "#171717", color: "#FFFFFF" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Export
        </button>
      </div>

      {/* Membership integration banner */}
      <div
        className="mb-6 p-4 rounded-xl flex items-start gap-4"
        style={{ backgroundColor: "rgba(0,251,169,0.06)", border: "1px solid rgba(0,251,169,0.2)" }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(0,251,169,0.15)" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00A87A" strokeWidth="2">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium" style={{ color: "#171717" }}>
            Membership Programme Integration
          </p>
          <p className="text-sm mt-0.5" style={{ color: "#6B6B6B" }}>
            {linkedCount} of {CUSTOMERS.length} customers have their Arc'teryx membership linked. ReBird credits and trade-in history sync automatically to their membership profile — enabling loyalty tier upgrades, targeted campaigns, and circular commerce insights.
          </p>
        </div>
        <button
          className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg font-medium"
          style={{ backgroundColor: "#171717", color: "#00FBA9" }}
        >
          Configure sync
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Customers", value: CUSTOMERS.length.toString(), sub: "+12% this quarter" },
          { label: "Credits Issued (lifetime)", value: fmtCredits(totalCreditsIssued), sub: "Across all tiers" },
          { label: "Total Customer Spend", value: fmt(totalSpend), sub: "Via ReBird purchases" },
          { label: "Membership Linked", value: `${linkedCount}/${CUSTOMERS.length}`, sub: `${Math.round((linkedCount / CUSTOMERS.length) * 100)}% connected` },
        ].map((stat) => (
          <div
            key={stat.label}
            className="p-4 rounded-xl"
            style={{ border: "1px solid rgba(169,169,169,0.15)", backgroundColor: "#FFFFFF" }}
          >
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>{stat.label}</p>
            <p className="text-xl font-light" style={{ color: "#171717" }}>{stat.value}</p>
            <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A9A9A9" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg outline-none"
            style={{ border: "1px solid rgba(169,169,169,0.2)", color: "#171717", backgroundColor: "#FFFFFF" }}
          />
        </div>
        <div className="flex gap-1">
          {["all", "Elite", "Plus", "Standard"].map((t) => (
            <button
              key={t}
              onClick={() => setTierFilter(t)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={
                tierFilter === t
                  ? { backgroundColor: "#171717", color: "#FFFFFF" }
                  : { color: "#A9A9A9", border: "1px solid rgba(169,169,169,0.2)" }
              }
            >
              {t === "all" ? "All tiers" : t}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(169,169,169,0.15)" }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: "#FAFAFA", borderBottom: "1px solid rgba(169,169,169,0.12)" }}>
              {["Customer", "Tier", "Trade-ins", "Credits balance", "Lifetime credits", "Spend", "Membership", "Last active"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: "#A9A9A9" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr
                key={c.id}
                onClick={() => setSelectedCustomer(c)}
                className="cursor-pointer transition-colors"
                style={{
                  borderBottom: i < filtered.length - 1 ? "1px solid rgba(169,169,169,0.08)" : "none",
                  backgroundColor: selectedCustomer?.id === c.id ? "rgba(0,251,169,0.04)" : "#FFFFFF",
                }}
                onMouseEnter={(e) => { if (selectedCustomer?.id !== c.id) (e.currentTarget as HTMLTableRowElement).style.backgroundColor = "#FAFAFA"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = selectedCustomer?.id === c.id ? "rgba(0,251,169,0.04)" : "#FFFFFF"; }}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium text-white"
                      style={{ backgroundColor: c.tier === "Elite" ? "#171717" : c.tier === "Plus" ? "#4A7FD4" : "#A9A9A9" }}
                    >
                      {c.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: "#171717" }}>{c.name}</p>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>{c.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><TierBadge tier={c.tier} /></td>
                <td className="px-4 py-3 text-sm" style={{ color: "#171717" }}>{c.tradeIns}</td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium" style={{ color: "#00A87A" }}>{fmtCredits(c.creditsBalance)}</span>
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: "#6B6B6B" }}>{fmtCredits(c.creditsLifetime)}</td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: "#171717" }}>{fmt(c.totalSpend)}</td>
                <td className="px-4 py-3">
                  {c.membershipLinked ? (
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#00FBA9" }} />
                      <span className="text-xs" style={{ color: "#6B6B6B" }}>{c.membershipId}</span>
                    </div>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(169,169,169,0.1)", color: "#A9A9A9" }}>
                      Not linked
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: "#A9A9A9" }}>{c.lastActivity}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-sm" style={{ color: "#A9A9A9" }}>No customers match your search</div>
        )}
      </div>

      {/* Customer detail panel */}
      {selectedCustomer && (
        <div
          className="fixed right-0 top-16 bottom-0 w-80 shadow-xl overflow-y-auto"
          style={{ backgroundColor: "#FFFFFF", borderLeft: "1px solid rgba(169,169,169,0.15)", zIndex: 40 }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-medium" style={{ color: "#171717" }}>Customer Profile</h3>
              <button onClick={() => setSelectedCustomer(null)} style={{ color: "#A9A9A9" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Avatar + name */}
            <div className="flex flex-col items-center mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-medium text-white mb-3"
                style={{ backgroundColor: selectedCustomer.tier === "Elite" ? "#171717" : selectedCustomer.tier === "Plus" ? "#4A7FD4" : "#A9A9A9" }}
              >
                {selectedCustomer.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <p className="font-medium" style={{ color: "#171717" }}>{selectedCustomer.name}</p>
              <p className="text-xs mt-0.5" style={{ color: "#A9A9A9" }}>{selectedCustomer.email}</p>
              <div className="mt-2"><TierBadge tier={selectedCustomer.tier} /></div>
              <p className="text-xs mt-2" style={{ color: "#A9A9A9" }}>Member since {selectedCustomer.joinedDate}</p>
            </div>

            {/* Stats */}
            <div className="space-y-3 mb-6">
              {[
                { label: "Credits balance", value: fmtCredits(selectedCustomer.creditsBalance), highlight: true },
                { label: "Lifetime credits earned", value: fmtCredits(selectedCustomer.creditsLifetime) },
                { label: "Total spend", value: fmt(selectedCustomer.totalSpend) },
                { label: "Trade-ins submitted", value: selectedCustomer.tradeIns.toString() },
                { label: "Last activity", value: selectedCustomer.lastActivity },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "#A9A9A9" }}>{row.label}</span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: row.highlight ? "#00A87A" : "#171717" }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ borderTop: "1px solid rgba(169,169,169,0.12)" }} className="pt-4 mb-6">
              <p className="text-xs font-medium mb-3" style={{ color: "#A9A9A9" }}>MEMBERSHIP</p>
              {selectedCustomer.membershipLinked ? (
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: "rgba(0,251,169,0.06)", border: "1px solid rgba(0,251,169,0.2)" }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#00FBA9" }} />
                    <span className="text-xs font-medium" style={{ color: "#00A87A" }}>Linked</span>
                  </div>
                  <p className="text-xs" style={{ color: "#6B6B6B" }}>
                    Arc'teryx ID <span className="font-mono font-medium">{selectedCustomer.membershipId}</span>
                  </p>
                  <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>Credits and trade-in history syncing to membership profile</p>
                </div>
              ) : (
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: "#FAFAFA", border: "1px solid rgba(169,169,169,0.15)" }}
                >
                  <p className="text-xs" style={{ color: "#A9A9A9" }}>
                    No membership account linked. Send an invite to connect this customer's ReBird activity to their Arc'teryx profile.
                  </p>
                  <button
                    className="mt-2 text-xs px-3 py-1.5 rounded-lg font-medium w-full"
                    style={{ backgroundColor: "#171717", color: "#FFFFFF" }}
                  >
                    Send link invite
                  </button>
                </div>
              )}
            </div>

            {/* Trade-in history */}
            <div>
              <p className="text-xs font-medium mb-3" style={{ color: "#A9A9A9" }}>TRADE-IN HISTORY</p>
              <div className="space-y-2">
                {Array.from({ length: selectedCustomer.tradeIns }).map((_, i) => {
                  const items = ["Beta AR Jacket", "Atom LT Hoody", "Alpha SV Jacket", "Gamma LT Pant", "Zeta SL Jacket", "Proton FL Hoody", "Cerium LT Hoody"];
                  const item = items[(selectedCustomer.id.charCodeAt(1) + i) % items.length];
                  const values = [148, 72, 201, 53, 89, 76, 98];
                  const val = values[(selectedCustomer.id.charCodeAt(1) + i) % values.length];
                  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                  const month = months[(selectedCustomer.id.charCodeAt(1) * 3 + i * 7) % 12];
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between p-2.5 rounded-lg"
                      style={{ backgroundColor: "#FAFAFA", border: "1px solid rgba(169,169,169,0.1)" }}
                    >
                      <div>
                        <p className="text-xs font-medium" style={{ color: "#171717" }}>{item}</p>
                        <p className="text-xs" style={{ color: "#A9A9A9" }}>{month} 2024</p>
                      </div>
                      <span className="text-xs font-medium" style={{ color: "#00A87A" }}>+${val} credit</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
